/* ==========================================================================
   ppl2-hero.js — pp-layout2 product hero
   Amazon-style UI (same as amz-product-hero) but checkout goes through the
   Shopify cart exactly like common-p-template (c-pp2-allLogic):

     variant id  → hidden .atcBtnDrawer proxy (data-atc + data-selection)
                 → custom-cart-drawer(-v2).js adds it with the "Selection"
                   property and opens the drawer.

   Same cart rules as c-pp2-allLogic:
     - Selection property: "<plug> / <years>" (raw title segments 0 and 3)
     - CA market: the PC unit product (/products/pcXX.js, first variant) is
       added instead of the vehicle variant; falls back to the variant.
     - Pedal disambiguation (pedal-disambiguation.js): when a rule exists the
       visitor picks the pedal first; a different plug adds that PC unit.
     - Out-of-stock sockets (theme setting) cannot be added.

   Other ppl2-* sections listen to `ppl2:variant:change` (detail below) or
   read window.PPL2.current on init.
   ========================================================================== */
(() => {
  'use strict';

  const PPL2 = (window.PPL2 = window.PPL2 || {});
  const WIDTHS = [360, 540, 720, 900, 1200, 1500];
  const SIZES = '(max-width: 768px) 100vw, (max-width: 1024px) 40vw, 560px';
  const mqMobile = window.matchMedia('(max-width: 768px)');
  const mqFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* ---------- helpers ---------- */
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const sized = (url, w) => {
    if (!url) return '';
    const base = String(url).replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    return base + (base.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  };
  const srcsetOf = (url) => WIDTHS.map((w) => sized(url, w) + ' ' + w + 'w').join(', ');

  const readJson = (root, sel, fallback) => {
    const el = root.querySelector(sel);
    if (!el) return fallback;
    try {
      const v = JSON.parse(el.textContent);
      return v == null ? fallback : v;
    } catch (e) {
      console.error('[ppl2-hero] invalid JSON in', sel, e);
      return fallback;
    }
  };

  /* same rule as snippets/replace-with-registered.liquid, for client-rendered text */
  const registered = (s) => String(s || '').replace(/(pedal commander)(?!\s*(®|&reg;))/gi, '$1®');

  /* "PC72 / Honda / Accord / 9th Gen (2013-2017) / (2.0L)" → "Honda Accord 9th Gen (2013-2017) (2.0L)" */
  const formatTitle = (raw) => {
    const parts = String(raw || '').split('/').slice(1).map((s) => s.trim()).filter(Boolean);
    return parts.length ? parts.join(' ') : String(raw || '');
  };

  const plugCode = (plug) => String(plug || '').split('-')[0].trim().toUpperCase();

  /* ---------- PC unit lookup (shared cache, same as c-pp2-allLogic) ---------- */
  const pcUnitCache = {};
  function pcUnitVariantId(plug) {
    const handle = String(plug || '').split('-')[0].trim().toLowerCase();
    if (!handle) return Promise.reject(new Error('Missing plug'));
    if (!pcUnitCache[handle]) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      pcUnitCache[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '.js')
        .then((res) => {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then((data) => {
          const first = (data.variants || [])[0];
          if (!first) throw new Error('No variant');
          return String(first.id);
        })
        .catch((err) => {
          delete pcUnitCache[handle];
          throw err;
        });
    }
    return pcUnitCache[handle];
  }

  /* pedal-disambiguation.js is deferred — wait until it had its chance to run */
  function pedalReady() {
    if (window.PC && window.PC.getPedalDisambiguationForVariant) return Promise.resolve();
    if (document.readyState === 'loading') {
      return new Promise((r) => document.addEventListener('DOMContentLoaded', r, { once: true }));
    }
    if (document.readyState === 'interactive') {
      return new Promise((r) => window.addEventListener('load', r, { once: true }));
    }
    return Promise.resolve();
  }

  /* The cart drawers bind `.atcBtnDrawer` once on load. Keep the first (bound)
     proxy on <body> so theme-editor re-renders of the section keep working. */
  function adoptProxy(root) {
    const own = root.querySelector('[data-ppl2-proxy]');
    const kept = document.getElementById('ppl2-atc-proxy');
    if (kept && kept !== own) {
      if (own) own.remove();
      return kept;
    }
    if (own) {
      own.id = 'ppl2-atc-proxy';
      document.body.appendChild(own);
    }
    return own;
  }

  /* resolves when a cart drawer reacts (opens) or after a timeout */
  function waitForDrawer(timeout) {
    return new Promise((resolve) => {
      const targets = [document.querySelector('custom-cart-drawer'), document.querySelector('.cart-drawer__outer')].filter(Boolean);
      let done = false;
      let mo = null;
      const finish = () => {
        if (done) return;
        done = true;
        if (mo) mo.disconnect();
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(finish, timeout || 4000);
      if (!targets.length) return;
      mo = new MutationObserver(() => {
        const v1 = document.querySelector('custom-cart-drawer');
        if (!v1 || getComputedStyle(v1).visibility === 'visible' || targets.length > 1) finish();
      });
      targets.forEach((t) => mo.observe(t, { attributes: true, attributeFilter: ['style', 'class', 'open'] }));
    });
  }

  /* ======================================================================
     HERO
     ====================================================================== */
  function initHero(root) {
    if (!root || root.dataset.ppl2Init === '1') return;
    root.dataset.ppl2Init = '1';

    const cfg = readJson(root, '[data-ppl2-config]', {});
    const i18n = cfg.i18n || {};
    const variants = readJson(root, '[data-ppl2-variants]', []);
    const globals = readJson(root, '[data-ppl2-globals]', []).filter(Boolean);
    if (!variants.length) return;

    const byId = new Map(variants.map((v) => [String(v.id), v]));

    const q = (s) => root.querySelector(s);
    const els = {
      track: q('[data-ppl2-track]'),
      thumbs: q('[data-ppl2-thumbs]'),
      counter: q('[data-ppl2-counter]'),
      title: q('[data-ppl2-title]'),
      price: q('[data-ppl2-price]'),
      was: q('[data-ppl2-was]'),
      wasValue: q('[data-ppl2-was-value]'),
      save: q('[data-ppl2-save]'),
      summaryPrice: q('[data-ppl2-summary-price]'),
      summaryWas: q('[data-ppl2-summary-was]'),
      stock: q('[data-ppl2-stock]'),
      selection: q('[data-ppl2-selection]'),
      current: q('[data-ppl2-variant-current]'),
      list: q('[data-ppl2-variant-list]'),
      more: q('[data-ppl2-more]'),
      filter: q('[data-ppl2-filter]'),
      filterEmpty: q('[data-ppl2-filter-empty]'),
      about: q('[data-ppl2-about]'),
      fallback: q('[data-ppl2-about-fallback]'),
      buybox: q('[data-ppl2-buybox]'),
      atc: q('[data-ppl2-atc]'),
      error: q('[data-ppl2-error]'),
      sticky: q('[data-ppl2-sticky]'),
      stickyAtc: q('[data-ppl2-sticky-atc]'),
      stickyPrice: q('[data-ppl2-sticky-price]'),
      stickySel: q('[data-ppl2-sticky-selection]'),
      pedalStep: root.querySelector('#pedalDisambiguationStep'),
    };
    const proxy = adoptProxy(root);
    const atcButtons = [els.atc, els.stickyAtc].filter(Boolean);
    const cleanups = [];

    let current = null;
    let images = [];
    let index = 0;
    let cartToken = 0;
    let cart = { id: '', selection: '' };
    let busy = false;
    /* set by the mobile sticky bar below — re-checks it when the add-to-cart state changes */
    let refreshSticky = () => {};

    /* ---------------- gallery ---------------- */
    /* Gallery = [variant's 4 images] + [tail]
       - the variant's images only when it has a model_based image (slot 0)
       - tail (data-ppl2-globals, same for every variant): the product's own images
         (first skipped) for top sellers, otherwise the global image blocks
       No featured-image fallback. */
    function galleryFor(v) {
      const slots = (v && v.images) || [];
      const own = slots[0] ? slots.filter(Boolean) : [];
      return own.concat(globals);
    }

    function renderGallery(v) {
      const next = galleryFor(v);
      /* same images (e.g. two variants without a model_based image) → leave the gallery as it is */
      if (next.length === images.length && next.every((u, i) => u === images[i])) return;
      images = next;
      const alt = esc(v ? formatTitle(v.title) : cfg.productTitle);
      els.track.innerHTML = images
        .map(
          (u, i) =>
            '<div class="ppl2-hero__slide" data-index="' + i + '"><img src="' + esc(sized(u, 900)) + '" srcset="' + esc(srcsetOf(u)) +
            '" sizes="' + SIZES + '" alt="' + alt + '" width="1000" height="1000" decoding="async" draggable="false"' +
            (i === 0 ? ' fetchpriority="high"' : ' loading="lazy"') + '></div>'
        )
        .join('');
      els.thumbs.innerHTML = images
        .map(
          (u, i) =>
            '<button type="button" class="ppl2-hero__thumb' + (i === 0 ? ' is-active' : '') + '" data-ppl2-thumb data-index="' + i +
            '" aria-label="' + esc((i18n.showImage || 'Show image') + ' ' + (i + 1)) + '"' + (i === 0 ? ' aria-current="true"' : '') +
            '><img src="' + esc(sized(u, 120)) + '" alt="" width="60" height="60" loading="lazy"></button>'
        )
        .join('');
      els.thumbs.hidden = images.length < 2;
      index = 0;
      els.track.scrollLeft = 0;
      syncThumbs();
    }

    function syncThumbs() {
      const thumbs = els.thumbs.querySelectorAll('[data-ppl2-thumb]');
      thumbs.forEach((t, i) => {
        const on = i === index;
        t.classList.toggle('is-active', on);
        if (on) {
          t.setAttribute('aria-current', 'true');
          revealInRail(t);
        } else {
          t.removeAttribute('aria-current');
        }
      });
      if (els.counter) {
        els.counter.textContent = images.length > 1 ? index + 1 + ' / ' + images.length : '';
        els.counter.hidden = images.length < 2;
      }
    }

    /* scroll the thumb rail only (never the page) */
    function revealInRail(thumb) {
      const rail = els.thumbs;
      if (rail.scrollHeight > rail.clientHeight + 1) {
        const top = thumb.offsetTop - rail.offsetTop;
        if (top < rail.scrollTop) rail.scrollTop = top;
        else if (top + thumb.offsetHeight > rail.scrollTop + rail.clientHeight) rail.scrollTop = top + thumb.offsetHeight - rail.clientHeight;
      }
      if (rail.scrollWidth > rail.clientWidth + 1) {
        const left = thumb.offsetLeft - rail.offsetLeft;
        if (left < rail.scrollLeft) rail.scrollLeft = left;
        else if (left + thumb.offsetWidth > rail.scrollLeft + rail.clientWidth) rail.scrollLeft = left + thumb.offsetWidth - rail.clientWidth;
      }
    }

    function goTo(i, smooth) {
      if (!images.length) return;
      index = Math.max(0, Math.min(images.length - 1, i));
      const dir = getComputedStyle(els.track).direction === 'rtl' ? -1 : 1;
      els.track.scrollTo({ left: dir * index * els.track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      syncThumbs();
    }

    let scrollRaf = 0;
    els.track.addEventListener(
      'scroll',
      () => {
        if (scrollRaf) return;
        scrollRaf = requestAnimationFrame(() => {
          scrollRaf = 0;
          const w = els.track.clientWidth || 1;
          const i = Math.round(Math.abs(els.track.scrollLeft) / w);
          if (i !== index && i < images.length) {
            index = i;
            syncThumbs();
          }
        });
      },
      { passive: true }
    );

    els.thumbs.addEventListener('click', (e) => {
      const t = e.target.closest('[data-ppl2-thumb]');
      if (t) goTo(+t.dataset.index, false);
    });
    els.thumbs.addEventListener('mouseover', (e) => {
      if (!mqFinePointer.matches) return;
      const t = e.target.closest('[data-ppl2-thumb]');
      if (t && +t.dataset.index !== index) goTo(+t.dataset.index, false);
    });
    els.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1, true); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1, true); }
    });

    /* hover zoom (desktop, fine pointer only) */
    if (cfg.zoom) {
      root.dataset.zoom = 'true';
      els.track.addEventListener('mousemove', (e) => {
        if (!mqFinePointer.matches || mqMobile.matches) return;
        const slide = e.target.closest('.ppl2-hero__slide');
        if (!slide) return;
        const img = slide.querySelector('img');
        const r = slide.getBoundingClientRect();
        img.style.transformOrigin = ((e.clientX - r.left) / r.width) * 100 + '% ' + ((e.clientY - r.top) / r.height) * 100 + '%';
        slide.classList.add('is-zoomed');
      });
      els.track.addEventListener('mouseleave', () => {
        els.track.querySelectorAll('.is-zoomed').forEach((s) => s.classList.remove('is-zoomed'));
      });
    }

    /* ---------------- about this item ---------------- */
    function renderAbout(v) {
      if (!els.about) return;
      if (v && v.bullets && v.bullets.length) {
        els.about.innerHTML =
          '<ul class="ppl2-hero__bullets">' + v.bullets.map((b) => '<li>' + registered(esc(b)) + '</li>').join('') + '</ul>';
      } else {
        els.about.innerHTML = els.fallback ? els.fallback.innerHTML : '';
      }
    }

    /* ---------------- variant picker: collapse + filter ---------------- */
    function setCollapsed(on) {
      if (!els.list) return;
      els.list.classList.toggle('is-collapsed', on);
      if (els.more) {
        els.more.setAttribute('aria-expanded', on ? 'false' : 'true');
        els.more.textContent = on ? els.more.dataset.showText : els.more.dataset.hideText;
      }
    }
    if (els.more) els.more.addEventListener('click', () => setCollapsed(!els.list.classList.contains('is-collapsed')));

    if (els.filter) {
      els.filter.addEventListener('input', () => {
        const term = els.filter.value.trim().toLowerCase();
        let shown = 0;
        els.list.classList.toggle('is-filtering', !!term);
        els.list.querySelectorAll('[data-ppl2-variant]').forEach((b) => {
          const hit = !term || b.dataset.search.indexOf(term) !== -1;
          b.classList.toggle('is-filtered-out', !hit);
          if (hit) shown++;
        });
        if (els.filterEmpty) els.filterEmpty.hidden = shown > 0;
        if (els.more) els.more.hidden = !!term;
      });
    }

    if (els.list) {
      els.list.addEventListener('click', (e) => {
        const b = e.target.closest('[data-ppl2-variant]');
        if (b) select(b.dataset.variantId);
      });
      /* radiogroup arrow-key navigation */
      els.list.addEventListener('keydown', (e) => {
        if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].indexOf(e.key) === -1) return;
        const btns = Array.from(els.list.querySelectorAll('[data-ppl2-variant]')).filter((b) => b.offsetParent !== null);
        const at = btns.indexOf(document.activeElement);
        if (at === -1) return;
        e.preventDefault();
        const next = btns[(at + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1) + btns.length) % btns.length];
        next.focus();
        select(next.dataset.variantId);
      });
    }

    /* ---------------- add to cart state ---------------- */
    const LABELS = {
      ready: i18n.atc,
      loading: i18n.atc,
      none: i18n.select,
      oos: i18n.oos,
      choose: i18n.choose,
      error: i18n.atc,
    };

    function setAtc(state, id, selection) {
      root.dataset.atcState = state;
      cart = { id: id || '', selection: selection || '' };
      atcButtons.forEach((b) => {
        /* "none" / "choose" stay clickable: the click scrolls to what is missing */
        b.disabled = state === 'oos' || state === 'loading';
        b.setAttribute('aria-disabled', state === 'ready' ? 'false' : 'true');
        const label = b.querySelector('.ppl2-btn__label');
        if (label && LABELS[state]) label.textContent = LABELS[state];
      });
      if (els.error) els.error.hidden = state !== 'error';
      if (els.stock) {
        els.stock.hidden = state === 'none';
        els.stock.textContent = state === 'oos' ? i18n.oosLong || i18n.oos : i18n.inStock || '';
      }
      refreshSticky();
    }

    function hidePedalStep() {
      if (!els.pedalStep) return;
      els.pedalStep.style.display = 'none';
      els.pedalStep.innerHTML = '';
    }

    async function resolveCart(v) {
      const token = ++cartToken;
      hidePedalStep();
      if (v.oos) return setAtc('oos');
      setAtc('loading');

      let chosen = '';
      if (cfg.pedalStep && els.pedalStep) {
        await pedalReady();
        if (token !== cartToken) return;
        const rule = window.PC && window.PC.getPedalDisambiguationForVariant ? window.PC.getPedalDisambiguationForVariant(v.id, cfg.market) : null;
        if (rule) {
          setAtc('choose');
          const plug = await new Promise((resolve) => window.PC.renderPedalDisambiguation(rule, cfg.market, resolve));
          if (token !== cartToken) return;
          chosen = (String(plug).match(/pc\d+/i) || [''])[0].toUpperCase();
          setAtc('loading');
        }
      }

      let selection = v.selection;
      let id = String(v.id);
      try {
        if (chosen && chosen !== plugCode(v.plug)) {
          /* "PC55 / BT (2020-2025)" → "PC63 / BT (2020-2025)" */
          selection = [chosen].concat(String(v.selection).split('/').slice(1).map((p) => p.trim())).join(' / ');
          id = await pcUnitVariantId(chosen);
        } else if (cfg.cartFromPcUnit) {
          try {
            id = await pcUnitVariantId(v.plug);
          } catch (err) {
            console.warn('[ppl2-hero] PC unit not found, adding the vehicle variant instead', v.plug, err);
            id = String(v.id);
          }
        }
      } catch (err) {
        console.error('[ppl2-hero] PC unit not found for the chosen pedal', chosen, err);
        if (token === cartToken) setAtc('error');
        return;
      }
      if (token !== cartToken) return;
      setAtc('ready', id, selection);
    }

    function focusMissingStep() {
      const target = root.dataset.atcState === 'choose' ? els.pedalStep : q('.ppl2-hero__variants') || els.buybox;
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function addToCart(btn) {
      if (busy) return;
      if (root.dataset.atcState !== 'ready' || !cart.id || !proxy) {
        focusMissingStep();
        return;
      }
      busy = true;
      atcButtons.forEach((b) => b.classList.add('is-loading'));
      proxy.dataset.atc = cart.id;
      proxy.dataset.selection = cart.selection;
      proxy.click();
      document.dispatchEvent(
        new CustomEvent('ppl2:cart:add', { detail: { id: cart.id, variantId: current && current.id, selection: cart.selection, source: btn === els.stickyAtc ? 'sticky' : 'buybox' } })
      );
      waitForDrawer(4000).then(() => {
        busy = false;
        atcButtons.forEach((b) => b.classList.remove('is-loading'));
      });
    }
    atcButtons.forEach((b) => b.addEventListener('click', () => addToCart(b)));

    /* ---------------- select a variant ---------------- */
    function updateUrl(id) {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('variant') !== String(id)) {
          url.searchParams.set('variant', id);
          window.history.replaceState(window.history.state, '', url.toString());
        }
      } catch (_) {}
    }

    function select(id, opts) {
      opts = opts || {};
      const v = byId.get(String(id));
      if (!v) return;
      const isBoot = !current;
      if (!isBoot && current.id === v.id) return;
      current = v;

      let activeBtn = null;
      if (els.list) {
        els.list.querySelectorAll('[data-ppl2-variant]').forEach((b) => {
          const on = b.dataset.variantId === String(v.id);
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
          if (on) activeBtn = b;
        });
      }
      if (activeBtn && activeBtn.hasAttribute('data-extra')) setCollapsed(false);

      /* server already rendered this variant's gallery / bullets — skip on boot */
      if (!(isBoot && String(v.id) === root.dataset.renderedVariant)) {
        renderGallery(v);
        renderAbout(v);
      }

      const label = v.label || formatTitle(v.title);
      if (els.title) els.title.innerHTML = registered(esc(formatTitle(v.title)));
      if (els.current) els.current.textContent = v.years || '';
      if (els.selection) els.selection.textContent = label;
      if (els.price) els.price.textContent = v.price;
      if (els.save) {
        els.save.hidden = !v.save;
        els.save.textContent = v.save ? '-' + v.save + '%' : '';
      }
      if (els.was) {
        els.was.hidden = !v.save;
        if (els.wasValue) els.wasValue.textContent = v.save ? v.compareAt : '';
      }
      if (els.summaryPrice) els.summaryPrice.textContent = v.price;
      if (els.summaryWas) {
        els.summaryWas.hidden = !v.save;
        els.summaryWas.textContent = v.save ? v.compareAt : '';
      }
      if (els.stickyPrice) els.stickyPrice.textContent = v.price;
      if (els.stickySel) els.stickySel.textContent = label;

      resolveCart(v);
      if (!opts.silent) updateUrl(v.id);

      const detail = {
        id: v.id,
        sku: v.sku,
        title: v.title,
        label: label,
        plug: v.plug,
        selection: v.selection,
        /* 4 slots: model_based, infoSection1, infoSection6, infoSection4 (null when blank) */
        slots: (v.images || []).slice(0, 4),
        images: (v.images || []).filter(Boolean),
        silent: !!opts.silent,
      };
      PPL2.current = detail;
      document.dispatchEvent(new CustomEvent('ppl2:variant:change', { detail: detail }));
    }

    /* ---------------- stable title height ----------------
       Titles differ per variant (1–3 lines); reserve the tallest one so the
       picker / bullets below never jump. Measured once off-screen, again
       only when the title's width changes. */
    function lockTitleHeight() {
      if (!els.title || variants.length < 2) return;
      const width = els.title.clientWidth;
      if (!width || width === lockTitleHeight.width) return;
      lockTitleHeight.width = width;
      const probe = els.title.cloneNode(false);
      probe.removeAttribute('data-ppl2-title');
      probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;left:-9999px;top:0;min-height:0;width:' + width + 'px';
      els.title.parentNode.appendChild(probe);
      let max = 0;
      variants.forEach((v) => {
        probe.textContent = registered(formatTitle(v.title));
        max = Math.max(max, probe.offsetHeight);
      });
      probe.remove();
      if (max) els.title.style.minHeight = max + 'px';
    }
    if (els.title) {
      if ('ResizeObserver' in window) {
        let raf = 0;
        const ro = new ResizeObserver(() => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(lockTitleHeight);
        });
        ro.observe(els.title);
        cleanups.push(() => ro.disconnect());
      } else {
        lockTitleHeight();
      }
    }

    /* ---------------- boot ---------------- */
    function initialId() {
      try {
        const fromUrl = new URL(window.location.href).searchParams.get('variant');
        if (fromUrl && byId.has(fromUrl)) return fromUrl;
      } catch (_) {}
      /* home page / listing hand-off (same localStorage key as c-pp2-allLogic) */
      let hp = null;
      try {
        hp = JSON.parse(localStorage.getItem('hpValues'));
      } catch (_) {}
      try {
        localStorage.removeItem('hpValues');
      } catch (_) {}
      if (hp && hp.id && byId.has(String(hp.id))) return String(hp.id);
      const server = root.dataset.initialVariant;
      return server && byId.has(server) ? server : null;
    }

    images = galleryFor(byId.get(String(root.dataset.renderedVariant)));
    syncThumbs();

    const startId = initialId();
    if (startId) {
      select(startId, { silent: startId === root.dataset.initialVariant });
    } else {
      setAtc('none');
      if (els.stickySel) els.stickySel.textContent = i18n.select || '';
    }

    /* ---------------- mobile sticky add-to-cart ----------------
       The bar is its own fixed element; the observed button never moves,
       so there is no stick/unstick feedback loop.
       Shown whenever the buy box button is off screen and either a vehicle is
       selected (the button is usually still below the picker then) or the
       visitor has scrolled past it. */
    if (cfg.sticky && els.sticky && els.atc && 'IntersectionObserver' in window) {
      let last = null;

      /* Richpanel chat launcher — same signal as common-p-template
         (c-pp-dropdown-img-gallery.js): `.richpanel-micro` present and NOT
         `.rp-micro-app-hide` → keep the launcher's corner free (common-p uses
         right: 80px); otherwise the button goes all the way to the edge.
         The widget is injected late, so poll until it appears (max 15s), then
         follow its class changes so hiding / showing it updates the bar live. */
      const RP_GAP = 72; // + the bar's own 14px padding ≈ common-p's 80px
      let rpEl = null;
      let rpMo = null;
      const updateSafeRight = () => {
        const on = !!(rpEl && rpEl.isConnected && !rpEl.classList.contains('rp-micro-app-hide'));
        els.sticky.style.setProperty('--ppl2-safe-right', on ? RP_GAP + 'px' : '0px');
      };
      const attachRichpanel = () => {
        const el = document.querySelector('.richpanel-micro');
        if (!el) return false;
        if (el !== rpEl) {
          rpEl = el;
          if (rpMo) rpMo.disconnect();
          rpMo = new MutationObserver(updateSafeRight);
          rpMo.observe(el, { attributes: true, attributeFilter: ['class', 'style'] });
        }
        updateSafeRight();
        return true;
      };
      let rpTries = 0;
      const rpPoll = attachRichpanel()
        ? 0
        : setInterval(() => {
            if (attachRichpanel() || ++rpTries >= 15) clearInterval(rpPoll);
          }, 1000);
      cleanups.push(() => {
        clearInterval(rpPoll);
        if (rpMo) rpMo.disconnect();
      });

      const evaluate = () => {
        const selected = root.dataset.atcState !== 'none';
        const show = !!(mqMobile.matches && last && !last.isIntersecting && (selected || last.boundingClientRect.top < 0));
        if (show && !els.sticky.classList.contains('is-visible') && !attachRichpanel()) updateSafeRight();
        els.sticky.classList.toggle('is-visible', show);
        els.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      };
      const io = new IntersectionObserver((entries) => {
        last = entries[entries.length - 1];
        evaluate();
      });
      io.observe(els.atc);
      refreshSticky = evaluate;
      mqMobile.addEventListener ? mqMobile.addEventListener('change', evaluate) : mqMobile.addListener(evaluate);
      cleanups.push(() => io.disconnect());
    }

    root.__ppl2Cleanup = () => cleanups.forEach((fn) => fn());
  }

  /* ======================================================================
     iOS scroll-lock failsafe (ported from amz-product-hero)
     Third-party widgets (Loox / Alia) can leave <body> scroll-locked on iOS.
     Releases such locks unless one of the theme's own drawers is really open.
     ====================================================================== */
  function scrollLockFailsafe() {
    const isIOS = /iP(hone|od|ad)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (!isIOS || PPL2.__iosFailsafe) return;
    PPL2.__iosFailsafe = true;

    const candidates = () =>
      [document.documentElement, document.body, document.getElementById('MainContent'), document.querySelector('.page-container')].filter(Boolean);

    const legitimateLock = () => {
      const cart = document.querySelector('custom-cart-drawer');
      if (cart && getComputedStyle(cart).visibility === 'visible') return true;
      if (document.querySelector('.cart-drawer__outer.active, .cart-drawer__outer.is-open, .cart-drawer__outer[open]')) return true;
      if (document.querySelector('menu-drawer details[open], header-drawer details[open]')) return true;
      if (document.querySelector('modal-dialog[open]')) return true;
      if (document.querySelector('#pedalHelpModal[style*="flex"], #pedalHelpModal[style*="block"]')) return true;
      return false;
    };

    let forced = [];
    const clearForced = () => {
      forced.forEach((el) => {
        ['overflow', 'overflow-x', 'overflow-y', 'touch-action'].forEach((p) => el.style.removeProperty(p));
        if (el.style.getPropertyPriority('position') === 'important') el.style.removeProperty('position');
      });
      forced = [];
    };

    const check = () => {
      if (legitimateLock()) {
        if (forced.length) clearForced();
        return;
      }
      let released = false;
      candidates().forEach((el) => {
        const cs = getComputedStyle(el);
        if (el.style.overflow === 'hidden' || el.style.overflowY === 'hidden' || cs.overflowY === 'hidden') {
          el.style.setProperty('overflow', 'visible', 'important');
          el.style.setProperty('overflow-y', 'visible', 'important');
          el.style.setProperty('overflow-x', 'visible', 'important');
          released = true;
        }
        if (el.style.touchAction === 'none' || cs.touchAction === 'none') {
          el.style.setProperty('touch-action', 'auto', 'important');
          released = true;
        }
        if (el !== document.documentElement && el.style.position === 'fixed') {
          const y = Math.abs(parseInt(el.style.top, 10)) || 0;
          el.style.setProperty('position', 'static', 'important');
          el.style.top = '';
          if (y && el === document.body) window.scrollTo(0, y);
          released = true;
        }
        if (released && forced.indexOf(el) === -1) forced.push(el);
      });
      if (released) {
        void document.documentElement.offsetHeight;
        const y = window.scrollY;
        requestAnimationFrame(() => {
          window.scrollTo(window.scrollX, y + 1);
          requestAnimationFrame(() => window.scrollTo(window.scrollX, y));
        });
      }
    };

    let n = 0;
    const poll = setInterval(() => {
      check();
      if (++n >= 20) clearInterval(poll);
    }, 1000);
    const mo = new MutationObserver(check);
    candidates().forEach((el) => mo.observe(el, { attributes: true, attributeFilter: ['style', 'class'] }));
    setTimeout(() => mo.disconnect(), 20000);
    document.addEventListener('touchmove', check, { passive: true });
  }

  /* ======================================================================
     boot + theme editor
     ====================================================================== */
  function boot(scope) {
    (scope || document).querySelectorAll('[data-ppl2-hero]').forEach((root) => {
      initHero(root);
      if (root.dataset.iosFailsafe === 'true') scrollLockFailsafe();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();

  document.addEventListener('shopify:section:load', (e) => boot(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.querySelector('[data-ppl2-hero]');
    if (root && root.__ppl2Cleanup) root.__ppl2Cleanup();
  });

  PPL2.initHero = initHero;
})();
